import { useRef, useState } from "react";
import {
  Button,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import { Download, DocumentAdd } from "@carbon/icons-react";
import i18next from "../../i18n";
import {
  parseFile,
  generateTemplateBlob,
  summarizeResult,
  type ParseResult,
  type ImportSummary,
} from "./importTemplate";
import type { ItemRow } from "./NewPurchaseRequestV2";
import "./importFromFileModal.scss";

interface ImportFromFileModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  /** Called with the parsed rows once the user confirms the import. */
  onImport: (items: ItemRow[]) => void;
}

const TEMPLATE_FILE_NAME = "purchase-request-template.xlsx";

/**
 * Import-from-file dialog, rebuilt on Carbon's `ComposedModal` chrome (Task 6). The ExcelJS SSOT
 * (`importTemplate.ts`) is consumed unchanged — `parseFile`/`generateTemplateBlob`/`summarizeResult`
 * are called exactly as before, and nothing is created until the user reviews the parsed rows.
 *
 * DELIBERATE DEVIATION (disclosed): the spreadsheet picker stays a native `<label htmlFor>` + real
 * `<input type="file">` rather than Carbon's `FileUploader`. `e2e/purchase-requests.spec.ts` drives
 * it with `getByLabel("Spreadsheet file").setInputFiles(...)`, which needs the descriptive label
 * associated with the actual file input — an association Carbon's `FileUploader`/
 * `FileUploaderDropContainer` does not provide (its own label is the button caption, not an
 * external field label). Keeping the labelled native input preserves the e2e file contract exactly
 * and carries zero Tailwind; the rest of the modal is Carbon.
 */
const ImportFromFileModal = ({
  open,
  setOpen,
  onImport,
}: ImportFromFileModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const [result, setResult] = useState<ParseResult | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [errorCode, setErrorCode] = useState<
    ParseResult["error"] | "PARSE" | null
  >(null);

  const reset = () => {
    setIsParsing(false);
    setFileName("");
    setResult(null);
    setSummary(null);
    setErrorCode(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const close = () => {
    reset();
    setOpen(false);
  };

  const handleDownloadTemplate = async () => {
    const blob = await generateTemplateBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = TEMPLATE_FILE_NAME;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setSummary(null);
    setErrorCode(null);
    setIsParsing(true);
    try {
      const parsed = await parseFile(file);
      if (!parsed.ok) {
        setErrorCode(parsed.error ?? "PARSE");
      } else if (parsed.items.length === 0) {
        setErrorCode("EMPTY");
      } else {
        setResult(parsed);
        setSummary(summarizeResult(parsed));
      }
    } catch {
      setErrorCode("PARSE");
    } finally {
      setIsParsing(false);
    }
  };

  const handleConfirm = () => {
    if (!result) return;
    onImport(result.items);
    close();
  };

  const errorMessage = (): string => {
    if (errorCode === "MISSING_HEADERS") {
      return i18next.t("purchaseRequests.import.headersMismatch", {
        defaultValue:
          "This file doesn't match the template. Download the template and try again.",
      });
    }
    if (errorCode === "EMPTY") {
      return i18next.t("purchaseRequests.import.emptyFile", {
        defaultValue: "No rows were found in this file.",
      });
    }
    return i18next.t("purchaseRequests.import.parseError", {
      defaultValue: "We couldn't read this file. Make sure it's a valid .xlsx.",
    });
  };

  const title = i18next.t("purchaseRequests.import.title", {
    defaultValue: "Import from file",
  });

  return (
    <ComposedModal open={open} aria-label={title} onClose={close}>
      <ModalHeader title={title} />
      <ModalBody hasForm>
        <p className="import-modal__intro">
          {i18next.t("purchaseRequests.import.intro", {
            defaultValue:
              "Upload a spreadsheet to fill the form. Nothing is created until you review the items and click Create.",
          })}
        </p>

        <Button
          kind="ghost"
          size="sm"
          renderIcon={Download}
          onClick={handleDownloadTemplate}
        >
          {i18next.t("purchaseRequests.import.downloadTemplate", {
            defaultValue: "Download template (.xlsx)",
          })}
        </Button>

        <div className="import-modal__field">
          <label htmlFor="import-file" className="cds--label">
            {i18next.t("purchaseRequests.import.file", {
              defaultValue: "Spreadsheet file",
            })}{" "}
            *
          </label>
          <input
            id="import-file"
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
            className="import-modal__input"
          />
          {fileName && (
            <p className="import-modal__filename">
              <DocumentAdd size={16} />
              {fileName}
            </p>
          )}
        </div>

        {isParsing && (
          <InlineLoading
            description={i18next.t("purchaseRequests.import.reading", {
              defaultValue: "Reading file...",
            })}
          />
        )}

        {errorCode && !isParsing && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={errorMessage()}
          />
        )}

        {summary && !isParsing && (
          <div className="import-modal__summary">
            <p className="import-modal__summary-title">
              {i18next.t("purchaseRequests.import.rowsImported", {
                defaultValue: "{{count}} rows ready to review",
                count: summary.rows,
              })}
            </p>
            <ul>
              {summary.missingMaterial > 0 && (
                <li>
                  {i18next.t("purchaseRequests.import.needMaterial", {
                    defaultValue: "{{count}} need a material",
                    count: summary.missingMaterial,
                  })}
                </li>
              )}
              {summary.unknownUnit > 0 && (
                <li>
                  {i18next.t("purchaseRequests.import.unknownUnit", {
                    defaultValue: "{{count}} with an unrecognized unit",
                    count: summary.unknownUnit,
                  })}
                </li>
              )}
              {summary.unknownCurrency > 0 && (
                <li>
                  {i18next.t("purchaseRequests.import.unknownCurrency", {
                    defaultValue: "{{count}} with an unrecognized currency",
                    count: summary.unknownCurrency,
                  })}
                </li>
              )}
              {summary.invalid > 0 && (
                <li>
                  {i18next.t("purchaseRequests.import.invalidValues", {
                    defaultValue: "{{count}} with values we couldn't read",
                    count: summary.invalid,
                  })}
                </li>
              )}
            </ul>
            <p className="import-modal__summary-hint">
              {i18next.t("purchaseRequests.import.reviewHint", {
                defaultValue:
                  "Flagged rows stay editable in the form — fix them before creating.",
              })}
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter
        secondaryButtonText={i18next.t("cta.cancel")}
        primaryButtonText={i18next.t("purchaseRequests.import.fillForm", {
          defaultValue: "Fill form",
        })}
        primaryButtonDisabled={isParsing || !result}
        onRequestSubmit={handleConfirm}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default ImportFromFileModal;
